import { create } from "zustand";

import { trackEvent } from "@/lib/engagement";

/**
 * Event-scoped "recently added" highlight for the Curate paper table.
 *
 * A baseline corpus snapshot is armed when an import/trawl STARTS (trawls
 * stream papers in progressively, so a completion-time diff would miss
 * everything that landed mid-run). The highlight set is then derived live
 * as (current papers − baseline), so rows light up as they land.
 *
 * The highlight clears on: chip dismiss, the next import arming a fresh
 * baseline, or viewing a different concept (derivation is concept-guarded;
 * the stale baseline is simply inert until overwritten).
 */
interface RecentlyAddedState {
  conceptId: string | null;
  /** Paper ids present when the import/trawl started; null = inactive. */
  baselineIds: string[] | null;
  /** Human label of the import path ("Spot Trawl", "Zotero import", …). */
  source: string | null;
  dismissed: boolean;
  arm: (conceptId: string, currentIds: string[], source: string) => void;
  dismiss: () => void;
  reset: () => void;
}

export const useRecentlyAddedStore = create<RecentlyAddedState>((set) => ({
  conceptId: null,
  baselineIds: null,
  source: null,
  dismissed: false,
  arm: (conceptId, currentIds, source) => {
    // Funnel event: fires at import START for all five import paths (trawl,
    // snowball, spot trawl, Zotero, BibTeX) — arm() is their one shared site.
    trackEvent("import_started", { source, concept_id: conceptId });
    set({ conceptId, baselineIds: currentIds, source, dismissed: false });
  },
  dismiss: () => set({ dismissed: true }),
  reset: () =>
    set({ conceptId: null, baselineIds: null, source: null, dismissed: false }),
}));

// Shared empty result — keeps the derived prop referentially stable while
// inactive so the memo'd PaperTable isn't handed a fresh Set every refetch.
const EMPTY_SET: ReadonlySet<string> = new Set();

/** Pure derivation: which of `paperIds` arrived after the armed baseline. */
export function deriveRecentlyAddedIds(
  state: Pick<RecentlyAddedState, "conceptId" | "baselineIds" | "dismissed">,
  conceptId: string,
  paperIds: string[],
): Set<string> {
  if (
    state.dismissed ||
    state.baselineIds === null ||
    state.conceptId !== conceptId
  ) {
    return EMPTY_SET as Set<string>;
  }
  const baseline = new Set(state.baselineIds);
  const added = paperIds.filter((id) => !baseline.has(id));
  return added.length ? new Set(added) : (EMPTY_SET as Set<string>);
}

// Console handle for dev smokes — a real import is the only other way to
// arm the store, which makes visual QA of the highlight needlessly costly.
if (import.meta.env.DEV && typeof window !== "undefined") {
  (window as unknown as Record<string, unknown>).__recentlyAddedStore =
    useRecentlyAddedStore;
}
